import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Instagram, Facebook, Music2, MapPin, ArrowUpRight, X, Phone, Flame, Star } from "lucide-react";
import { JOBBER_BOOKING_URL } from "../lib/brand";

const DEFAULT_CONFIG = {
  hero_headline: "Real Results. Real Transformations.",
  hero_subtitle: "Real detailing results from Fuego Precision Cleaning Services LLC.",
  cta_text: "Book Your Appointment",
  cta_link: JOBBER_BOOKING_URL,
  cta_repeat_after: 6,
  phone_number: "",
  sticky_bar_enabled: true,
  sections: { hero: true, testimonials: true, sticky_bar: true },
};

const CtaButton = ({ href, children, testid, size = "md" }) => (
  <a
    href={href}
    data-testid={testid}
    className={`inline-flex items-center justify-center gap-2 bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase transition-colors ${
      size === "lg" ? "px-10 py-5 text-base" : "px-7 py-3 text-sm"
    }`}
  >
    <Flame size={size === "lg" ? 18 : 14} /> {children}
  </a>
);

const GalleryPage = () => {
  const [items, setItems] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [socials, setSocials] = useState({ instagram: "#", facebook: "#", tiktok: "#", google_business: "#" });
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery").then(r => setItems(r.data)).catch(() => {});
    api.get("/testimonials").then(r => setTestimonials(r.data)).catch(() => {});
    api.get("/gallery/config").then(r => setConfig({ ...DEFAULT_CONFIG, ...r.data, sections: { ...DEFAULT_CONFIG.sections, ...(r.data?.sections || {}) } })).catch(() => {});
    api.get("/socials").then(r => setSocials(r.data)).catch(() => {});
  }, []);

  // Escape closes lightbox
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Group items by category, preserving order
  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const key = (it.category && it.category.trim()) || "Uncategorized";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    for (const arr of map.values()) arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return Array.from(map.entries());
  }, [items]);

  const cta = config.cta_text || "Book Your Appointment";
  const link = config.cta_link || JOBBER_BOOKING_URL;
  const repeat = Math.max(1, parseInt(config.cta_repeat_after, 10) || 6);
  const showHero = config.sections?.hero !== false;
  const showTestis = config.sections?.testimonials !== false && testimonials.length > 0;
  const showSticky = config.sections?.sticky_bar !== false && config.sticky_bar_enabled !== false;

  return (
    <div data-testid="gallery-page" className={showSticky ? "pb-24 md:pb-0" : ""}>
      {showHero && (
        <section className="pt-24 pb-16 border-b border-white/10 relative overflow-hidden" data-testid="gallery-hero">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_#D90429_0%,_transparent_45%)] opacity-20 pointer-events-none" />
          <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative">
            <div className="font-display uppercase tracking-[0.3em] text-xs text-[#D90429] mb-4">The Gallery</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95]" data-testid="gallery-hero-headline">
              {config.hero_headline}
            </h1>
            <p className="mt-6 text-[#D4D4D4] max-w-2xl mx-auto text-base sm:text-lg" data-testid="gallery-hero-subtitle">
              {config.hero_subtitle}
            </p>
            <div className="mt-10">
              <CtaButton href={link} testid="gallery-hero-cta" size="lg">{cta}</CtaButton>
            </div>
          </div>
        </section>
      )}

      {/* Categorized grids with repeating CTAs + testimonials between categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-20">
          {grouped.length === 0 && (
            <div className="text-center text-[#A3A3A3] py-16">No gallery items yet — add some in Admin → Gallery.</div>
          )}
          {grouped.map(([category, catItems], catIdx) => (
            <div key={category} data-testid={`gallery-category-${category.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-baseline justify-between border-b border-[#262626] pb-4 mb-8">
                <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight text-white">{category}</h2>
                <span className="font-display uppercase tracking-[0.2em] text-[10px] text-[#A3A3A3]">{catItems.length} {catItems.length === 1 ? "job" : "jobs"}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {catItems.map((it, idx) => (
                  <React.Fragment key={it.id}>
                    <figure
                      className="group relative overflow-hidden border border-[#262626] hover:border-[#D90429] bg-black cursor-pointer transition-colors flex flex-col"
                      onClick={() => setLightbox(it)}
                      data-testid={`gallery-item-${it.id}`}
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-[#0a0a0a]">
                        <img
                          src={it.url}
                          alt={it.caption || it.vehicle || "Detail work"}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      </div>
                      <figcaption className="p-4 border-t border-[#262626] bg-[#0a0a0a] text-sm space-y-1">
                        {(it.caption || it.vehicle) && (
                          <div className="font-display uppercase tracking-wider text-white truncate">{it.caption || it.vehicle}</div>
                        )}
                        {it.service_performed && (
                          <div className="text-[#A3A3A3] text-xs"><span className="text-[#D90429]">Service:</span> {it.service_performed}</div>
                        )}
                        {it.result_summary && (
                          <div className="text-[#D4D4D4] text-xs italic line-clamp-2">"{it.result_summary}"</div>
                        )}
                      </figcaption>
                    </figure>

                    {(idx + 1) % repeat === 0 && idx !== catItems.length - 1 && (
                      <div className="sm:col-span-2 lg:col-span-3 border-2 border-[#D90429]/40 bg-gradient-to-r from-[#D90429]/10 to-transparent p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid={`gallery-repeat-cta-${catIdx}-${idx}`}>
                        <h3 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-tight">Ready for results like these?</h3>
                        <CtaButton href={link} testid={`gallery-repeat-cta-btn-${catIdx}-${idx}`}>{cta}</CtaButton>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Testimonial break between categories */}
              {showTestis && catIdx < grouped.length - 1 && testimonials[catIdx % testimonials.length] && (
                <TestimonialCard t={testimonials[catIdx % testimonials.length]} />
              )}

              {/* Full-width CTA after each category */}
              <div className="mt-10 border border-[#D90429]/50 bg-black p-8 md:p-10 text-center" data-testid={`gallery-category-cta-${catIdx}`}>
                <h3 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">Ready for results like these?</h3>
                <p className="mt-3 text-[#A3A3A3] text-sm max-w-xl mx-auto">Book online in under a minute — direct into our schedule.</p>
                <div className="mt-6"><CtaButton href={link} testid={`gallery-category-cta-btn-${catIdx}`} size="lg">{cta}</CtaButton></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Socials */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-4">Follow along</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">More work daily on socials.</h2>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={socials.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#262626] hover:border-[#D90429] px-5 py-3 transition-colors" data-testid="gallery-instagram-link"><Instagram size={16}/> Instagram <ArrowUpRight size={14}/></a>
            <a href={socials.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#262626] hover:border-[#D90429] px-5 py-3 transition-colors" data-testid="gallery-facebook-link"><Facebook size={16}/> Facebook <ArrowUpRight size={14}/></a>
            <a href={socials.tiktok} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#262626] hover:border-[#D90429] px-5 py-3 transition-colors" data-testid="gallery-tiktok-link"><Music2 size={16}/> TikTok <ArrowUpRight size={14}/></a>
            <a href={socials.google_business} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#262626] hover:border-[#D90429] px-5 py-3 transition-colors" data-testid="gallery-google-link"><MapPin size={16}/> Google Business <ArrowUpRight size={14}/></a>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-white hover:text-[#D90429] p-2"
            aria-label="Close"
            data-testid="gallery-lightbox-close"
          >
            <X size={32}/>
          </button>
          <div className="max-w-6xl w-full max-h-[90vh] flex flex-col md:flex-row gap-6" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.caption || ""} className="flex-1 min-h-0 object-contain max-h-[70vh] md:max-h-[85vh]" />
            <aside className="md:w-72 shrink-0 border border-[#262626] bg-[#0a0a0a] p-5 space-y-3 text-sm text-white overflow-y-auto max-h-[85vh]">
              {lightbox.category && <div className="font-display uppercase tracking-widest text-[10px] text-[#D90429]">{lightbox.category}</div>}
              {(lightbox.caption || lightbox.vehicle) && <h3 className="font-display uppercase tracking-tight text-xl">{lightbox.caption || lightbox.vehicle}</h3>}
              {lightbox.vehicle && lightbox.caption && lightbox.caption !== lightbox.vehicle && (
                <div><span className="text-[#D90429] uppercase text-[10px] tracking-widest block">Vehicle</span>{lightbox.vehicle}</div>
              )}
              {lightbox.service_performed && (
                <div><span className="text-[#D90429] uppercase text-[10px] tracking-widest block">Service Performed</span>{lightbox.service_performed}</div>
              )}
              {lightbox.result_summary && (
                <div><span className="text-[#D90429] uppercase text-[10px] tracking-widest block">Result</span><em>"{lightbox.result_summary}"</em></div>
              )}
              <CtaButton href={link} testid="gallery-lightbox-cta">{cta}</CtaButton>
            </aside>
          </div>
        </div>
      )}

      {/* Mobile sticky bar */}
      {showSticky && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-black border-t-2 border-[#D90429] p-3 flex gap-3" data-testid="mobile-sticky-bar">
          {config.phone_number && (
            <a
              href={`tel:${config.phone_number.replace(/[^\d+]/g, "")}`}
              data-testid="sticky-call-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 border border-white text-white font-display font-bold uppercase tracking-wider py-3 text-sm"
            >
              <Phone size={16}/> Call Now
            </a>
          )}
          <a
            href={link}
            data-testid="sticky-book-btn"
            className={`${config.phone_number ? "flex-1" : "w-full"} inline-flex items-center justify-center gap-2 bg-[#D90429] text-white font-display font-bold uppercase tracking-wider py-3 text-sm`}
          >
            <Flame size={16}/> Book Now
          </a>
        </div>
      )}
    </div>
  );
};

const TestimonialCard = ({ t }) => (
  <div className="mt-16 border-l-4 border-[#D90429] bg-[#0a0a0a] p-8 md:p-10 max-w-3xl mx-auto" data-testid={`testimonial-${t.id}`}>
    <div className="flex gap-1 mb-4">
      {[...Array(t.rating || 5)].map((_, i) => <Star key={i} size={18} className="text-[#D90429] fill-[#D90429]"/>)}
    </div>
    <blockquote className="font-display text-xl sm:text-2xl leading-snug text-white">"{t.quote}"</blockquote>
    <div className="mt-4 text-sm text-[#A3A3A3]">
      <span className="text-white font-bold">{t.name}</span>
      {t.vehicle && <span> · {t.vehicle}</span>}
    </div>
  </div>
);

export default GalleryPage;
